import { useEnvContext } from "context/EnvContext";
import {
  ACCOUNT_STATUS_ACTIVE,
  ACCOUNT_STATUS_INACTIVE,
  APP_VERSION_NAMES,
  CAREPROVIDER_DUPLICATE,
  CAREPROVIDER_INACTIVE,
  CAREPROVIDER_INVALID,
  CAREPROVIDER_TAB_DEFAULT,
  CAREPROVIDER_TRASH,
  REDIRECT_PARAM,
} from "core/consts";
import { Careprovider } from "core/types";
import InactiveProfilePage from "dsl/atoms/InactiveProfilePage";
import { ReactNode } from "react";
import { matchPath, Navigate, Outlet } from "react-router-dom";

const CAREPROVIDER_DASHBOARD_PATH_PATTERN =
  "/careprovider/dashboard/:careprovider_id/*";

export const getDefaultRedirectPath = (careproviderId: number) =>
  `/careprovider/dashboard/${careproviderId}/requests/${CAREPROVIDER_TAB_DEFAULT}`;

/** Extracts careprovider id from path for redirect checks. */
function getCareproviderIdFromDashboardPath(pathname: string): number | null {
  const match = matchPath(CAREPROVIDER_DASHBOARD_PATH_PATTERN, pathname);
  const idStr = match?.params?.careprovider_id;
  if (idStr == null) return null;
  const id = parseInt(idStr, 10);
  return Number.isNaN(id) ? null : id;
}

/** True when redirect_to targets another provider's dashboard; honouring it would cause an auth redirect loop. */
export function isRedirectToOtherCareproviderDashboard(
  redirectPath: string | undefined,
  loggedInCareproviderId: number,
): boolean {
  if (redirectPath == null || redirectPath === "") return false;
  const id = getCareproviderIdFromDashboardPath(redirectPath);
  return id !== null && id !== loggedInCareproviderId;
}

export type ResolvePostAuthRedirectParams = {
  customRedirectPath: string | undefined;
  loggedInCareproviderId: number;
  useDefaultRedirect: boolean;
};

/** Post-auth redirect path. Uses default when custom path is another provider's dashboard (avoids redirect loop). */
export function resolvePostAuthRedirectPath({
  customRedirectPath,
  loggedInCareproviderId,
  useDefaultRedirect,
}: ResolvePostAuthRedirectParams): string {
  const defaultRedirect = getDefaultRedirectPath(loggedInCareproviderId);
  const redirectTargetsOtherProvider = isRedirectToOtherCareproviderDashboard(
    customRedirectPath,
    loggedInCareproviderId,
  );
  const mustUseDefaultRedirect =
    useDefaultRedirect || redirectTargetsOtherProvider;

  if (redirectTargetsOtherProvider) {
    console.error(
      "[Receiver] User logged in with care provider",
      loggedInCareproviderId,
      "but redirect path targeted another provider's dashboard:",
      customRedirectPath,
      "- redirecting to own dashboard to avoid auth loop.",
    );
  }

  if (mustUseDefaultRedirect) return defaultRedirect;
  return customRedirectPath ?? defaultRedirect;
}

export const DebugPage = () => (
  <div style={{ flex: "1" }}>{`version: ${process.env.BUILD_VERSION}`}</div>
);

export function generateParams(
  params: Array<[string, number | string | null | undefined]>,
  inQueryParam?: string,
) {
  const queryParam = inQueryParam || "";
  return params.reduce((previous, current) => {
    const [key, value] = current;
    if (value == null) return previous;

    const prefix = previous === "" ? "?" : "&";
    return `${previous}${prefix}${key}=${value}`;
  }, queryParam);
}

export function RequireCondition({
  children,
  hasCondition,
  isNull,
  redirectTo,
}: {
  children?: React.JSX.Element | null;
  hasCondition: boolean;
  redirectTo?: string;
  isNull?: boolean;
}) {
  const { app } = useEnvContext();
  const to =
    redirectTo ?? `/${APP_VERSION_NAMES[app].replace("clinic", "app")}`;

  if (hasCondition) {
    return <>{children ? children : <Outlet />}</>;
  }

  if (isNull) {
    return null;
  }

  return <Navigate to={to} />;
}

export function ValidateCareprovider({
  careprovider,
  children,
  token,
}: {
  careprovider: Careprovider;
  children: ReactNode;
  token: string | undefined;
}) {
  if (
    careprovider.status == CAREPROVIDER_INACTIVE ||
    careprovider.status == CAREPROVIDER_INVALID ||
    careprovider.status == CAREPROVIDER_TRASH ||
    careprovider.status == CAREPROVIDER_DUPLICATE
  )
    return <InactiveProfilePage careproviderName={careprovider.name} />;

  // validate accounts:
  // if visiting with tokan and there are active accounts
  // or all of them are inactive, redirect to the login page
  const { accounts } = careprovider;
  if (token && accounts) {
    if (
      accounts.some((account) => account.status === ACCOUNT_STATUS_ACTIVE) ||
      accounts.every((account) => account.status === ACCOUNT_STATUS_INACTIVE)
    ) {
      const toAuthWithoutToken = `/careprovider/auth?${REDIRECT_PARAM}=${encodeURIComponent(
        `${location.pathname}`,
      )}&careproviderid=${careprovider.id}`;
      window.location.pathname = toAuthWithoutToken;
      return null;
    }
  }
  return <>{children}</>;
}
